const DEFAULT_BRAND_NAME = 'Ordeez Agent';
const DEFAULT_INITIAL_MESSAGE = 'Hi! How can I help you today?';
const DEFAULT_POPULAR_QUESTIONS = [
    'How can I get started?',
    'What services do you offer?',
    'I need help with my order',
];
const DEFAULT_APPEARANCE = {
    brandName: DEFAULT_BRAND_NAME,
    primaryColor: '#4F46E5',
    secondaryColor: '#7C3AED',
    fontColor: '#1F2937',
    initialMessage: DEFAULT_INITIAL_MESSAGE,
    popularQuestions: DEFAULT_POPULAR_QUESTIONS,
    avatarUrl: undefined,
    widgetPosition: 'left',
};
const NESTED_KEYS = ['appearance', 'customization', 'branding', 'ui', 'theme', 'widget'];
const COLOR_KEYS = {
    primary: [
        'primaryColor',
        'primary_color',
        'colors.primary',
        'palette.primary',
        'brandColor',
        'brand_color',
        'accent',
    ],
    secondary: [
        'secondaryColor',
        'secondary_color',
        'colors.secondary',
        'palette.secondary',
        'accentSecondary',
        'accent_secondary',
    ],
    font: ['fontColor', 'font_color', 'textColor', 'text_color', 'bodyColor', 'body_color'],
};
const BRAND_KEYS = [
    'brandName',
    'brand_name',
    'displayName',
    'display_name',
    'widgetTitle',
    'widget_title',
    'name',
];
const AVATAR_KEYS = [
    'avatarUrl',
    'avatar_url',
    'profilePicture',
    'profile_picture',
    'logoUrl',
    'logo_url',
    'image',
    'image_url',
];
const INITIAL_MESSAGE_KEYS = [
    'initialMessage',
    'initial_message',
    'welcomeMessage',
    'welcome_message',
    'greeting',
    'greeting_message',
    'defaultGreeting',
    'default_greeting',
];
const POPULAR_QUESTION_KEYS = [
    'popularQuestions',
    'popular_questions',
    'suggestedQuestions',
    'suggested_questions',
    'faqSuggestions',
    'faq_suggestions',
];
const WIDGET_POSITION_KEYS = ['widgetPosition', 'widget_position'];
function isRecord(value) {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}
function readPath(source, path) {
    if (!source)
        return undefined;
    const segments = path.split('.');
    let current = source;
    for (const segment of segments) {
        if (!isRecord(current) || !(segment in current)) {
            return undefined;
        }
        current = current[segment];
    }
    return current;
}
function coalesceString(sources, paths) {
    for (const path of paths) {
        for (const source of sources) {
            const value = readPath(source, path);
            if (typeof value === 'string') {
                const trimmed = value.trim();
                if (trimmed.length > 0) {
                    return trimmed;
                }
            }
        }
    }
    return undefined;
}
function coalesceStringArray(sources, paths) {
    for (const path of paths) {
        for (const source of sources) {
            const value = readPath(source, path);
            if (Array.isArray(value)) {
                const cleaned = value
                    .filter((item) => typeof item === 'string')
                    .map((item) => item.trim())
                    .filter((item) => item.length > 0);
                if (cleaned.length > 0) {
                    return cleaned;
                }
            }
        }
    }
    return [];
}
function normalizeColor(value, fallback) {
    if (!value)
        return fallback;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : fallback;
}
function normalizeWidgetPosition(value) {
    if (!value)
        return undefined;
    const cleaned = value.trim().toLowerCase();
    if (cleaned === 'left' || cleaned === 'center' || cleaned === 'right') {
        return cleaned;
    }
    return undefined;
}
function normalizeAppearanceObject(input) {
    if (!input)
        return {};
    const cleaned = {};
    if (typeof input.brandName === 'string' && input.brandName.trim()) {
        cleaned.brandName = input.brandName.trim();
    }
    if (typeof input.primaryColor === 'string' && input.primaryColor.trim()) {
        cleaned.primaryColor = input.primaryColor.trim();
    }
    if (typeof input.secondaryColor === 'string' && input.secondaryColor.trim()) {
        cleaned.secondaryColor = input.secondaryColor.trim();
    }
    if (typeof input.fontColor === 'string' && input.fontColor.trim()) {
        cleaned.fontColor = input.fontColor.trim();
    }
    if (typeof input.avatarUrl === 'string' && input.avatarUrl.trim()) {
        cleaned.avatarUrl = input.avatarUrl.trim();
    }
    if (typeof input.initialMessage === 'string' && input.initialMessage.trim()) {
        cleaned.initialMessage = input.initialMessage.trim();
    }
    if (Array.isArray(input.popularQuestions)) {
        const questions = input.popularQuestions
            .filter((q) => typeof q === 'string')
            .map((q) => q.trim())
            .filter((q) => q.length > 0);
        if (questions.length > 0) {
            cleaned.popularQuestions = Array.from(new Set(questions)).slice(0, 3);
        }
    }
    const widgetPosition = normalizeWidgetPosition(input.widgetPosition);
    if (widgetPosition) {
        cleaned.widgetPosition = widgetPosition;
    }
    return cleaned;
}
function normalizeAppearanceRecord(input) {
    const obj = normalizeAppearanceObject(input);
    return Object.keys(obj).length > 0 ? obj : null;
}
export function resolveAppearance(config) {
    var _a, _b, _c, _d, _e;
    if (!config || !isRecord(config.settings)) {
        const override = normalizeAppearanceObject(config === null || config === void 0 ? void 0 : config.appearance);
        const brandName = override.brandName || ((_a = config === null || config === void 0 ? void 0 : config.name) === null || _a === void 0 ? void 0 : _a.trim()) || DEFAULT_BRAND_NAME;
        return Object.assign(Object.assign(Object.assign({}, DEFAULT_APPEARANCE), override), { brandName, popularQuestions: override.popularQuestions && override.popularQuestions.length > 0
                ? override.popularQuestions
                : DEFAULT_APPEARANCE.popularQuestions, widgetPosition: (_b = override.widgetPosition) !== null && _b !== void 0 ? _b : DEFAULT_APPEARANCE.widgetPosition });
    }
    const baseSettings = config.settings;
    const appearanceOverrideRecord = normalizeAppearanceRecord(config.appearance);
    const sources = [];
    if (appearanceOverrideRecord) {
        sources.push(appearanceOverrideRecord);
    }
    sources.push(baseSettings);
    for (const key of NESTED_KEYS) {
        const nested = readPath(baseSettings, key);
        if (isRecord(nested)) {
            sources.push(nested);
        }
    }
    // Allow entire config as a final fallback
    sources.push({
        id: config.id,
        name: config.name,
        defaultGreeting: baseSettings === null || baseSettings === void 0 ? void 0 : baseSettings['defaultGreeting'],
        default_greeting: baseSettings === null || baseSettings === void 0 ? void 0 : baseSettings['default_greeting'],
    });
    const brandName = coalesceString(sources, BRAND_KEYS) || ((_c = config.name) === null || _c === void 0 ? void 0 : _c.trim()) || DEFAULT_BRAND_NAME;
    const avatarUrl = coalesceString(sources, AVATAR_KEYS);
    const primaryColor = normalizeColor(coalesceString(sources, COLOR_KEYS.primary), DEFAULT_APPEARANCE.primaryColor);
    const secondaryColor = normalizeColor(coalesceString(sources, COLOR_KEYS.secondary), DEFAULT_APPEARANCE.secondaryColor);
    const fontColor = normalizeColor(coalesceString(sources, COLOR_KEYS.font), DEFAULT_APPEARANCE.fontColor);
    const initialMessage = coalesceString(sources, INITIAL_MESSAGE_KEYS) ||
        coalesceString(sources, ['defaultGreeting', 'default_greeting']) ||
        DEFAULT_INITIAL_MESSAGE;
    const popularQuestions = coalesceStringArray(sources, POPULAR_QUESTION_KEYS);
    const dedupedQuestions = Array.from(new Set(popularQuestions)).slice(0, 3);
    const widgetPosition = normalizeWidgetPosition(coalesceString(sources, WIDGET_POSITION_KEYS)) ||
        normalizeWidgetPosition(typeof ((_d = config.appearance) === null || _d === void 0 ? void 0 : _d.widgetPosition) === 'string'
            ? (_e = config.appearance) === null || _e === void 0 ? void 0 : _e.widgetPosition
            : undefined) ||
        DEFAULT_APPEARANCE.widgetPosition;
    return {
        brandName,
        primaryColor,
        secondaryColor,
        fontColor,
        initialMessage,
        popularQuestions: dedupedQuestions.length > 0 ? dedupedQuestions : DEFAULT_POPULAR_QUESTIONS,
        avatarUrl,
        widgetPosition,
    };
}
export { DEFAULT_APPEARANCE };
